import React, { useRef, useState } from 'react';
import {
    Chat24Regular,
    ArrowDownload16Regular,
    MoreHorizontal16Regular,
} from '@fluentui/react-icons';
import { OverviewActionMenu } from './OverviewOverlays';
import InsightsNewEvidenceBanner from './InsightsNewEvidenceBanner';
import InsightsHistoricalVersionBanner from './InsightsHistoricalVersionBanner';
import styles from './intelligenceInsightsModules.module.css';
import overviewStyles from './intelligenceInsightsOverview.module.css';

/**
 * Shared Insights module page header (Figma: title / subtitle / meta + actions).
 */
const InsightsModuleChrome = ({
    title,
    subtitle,
    metaLine = 'Entire Project · Version 3 · Generated 27 Jul 2026',
    onAskAi,
    onExport,
    exportEnabled = false,
    moreMenuItems = null,
    onMoreMenuSelect,
    newEvidenceBanner = null,
    onNewEvidenceAction = null,
    historicalVersion = null,
    onExitHistoricalView = null,
    readOnly = false,
    children,
    demoBadge = false,
}) => {
    const moreRef = useRef(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const menuItems = Array.isArray(moreMenuItems) ? moreMenuItems : [];
    const showMoreMenu = menuItems.length > 0 && onMoreMenuSelect;

    return (
        <div className={`${styles.moduleRoot} ${readOnly ? styles.moduleRootReadOnly : ''}`}>
            <header className={styles.header}>
                <div className={styles.headerTop}>
                    <div>
                        <div className={styles.titleRow}>
                            <h1 className={styles.title}>{title}</h1>
                            {demoBadge ? (
                                <span className={styles.demoBadge}>Design preview</span>
                            ) : null}
                        </div>
                        {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
                        {metaLine ? <p className={styles.meta}>{metaLine}</p> : null}
                    </div>
                    <div className={styles.headerActions}>
                        <button
                            type="button"
                            className={`${styles.btnAsk} ${
                                readOnly ? styles.headerActionDisabled : ''
                            }`}
                            onClick={onAskAi}
                            disabled={readOnly}
                            aria-disabled={readOnly || undefined}>
                            <Chat24Regular className={styles.btnIcon} aria-hidden />
                            Ask Lens
                        </button>
                        {exportEnabled && onExport ? (
                            <button
                                type="button"
                                className={styles.btnGhost}
                                onClick={onExport}>
                                <ArrowDownload16Regular className={styles.btnIcon} aria-hidden />
                                Export PDF
                            </button>
                        ) : null}
                        {showMoreMenu ? (
                            <div className={overviewStyles.splitWrap} ref={moreRef}>
                                <button
                                    type="button"
                                    className={`${styles.btnIconOnly} ${
                                        readOnly ? styles.headerActionDisabled : ''
                                    }`}
                                    aria-label="More actions"
                                    aria-expanded={menuOpen}
                                    aria-haspopup="menu"
                                    disabled={readOnly}
                                    aria-disabled={readOnly || undefined}
                                    onClick={() => setMenuOpen(open => !open)}>
                                    <MoreHorizontal16Regular />
                                </button>
                                <OverviewActionMenu
                                    open={menuOpen}
                                    anchorRef={moreRef}
                                    items={menuItems}
                                    onClose={() => setMenuOpen(false)}
                                    onSelect={onMoreMenuSelect}
                                />
                            </div>
                        ) : null}
                    </div>
                </div>
            </header>
            {readOnly && historicalVersion ? (
                <InsightsHistoricalVersionBanner
                    version={historicalVersion}
                    onReturnToCurrent={onExitHistoricalView}
                />
            ) : null}
            {!readOnly && newEvidenceBanner ? (
                <InsightsNewEvidenceBanner
                    banner={newEvidenceBanner}
                    onAction={onNewEvidenceAction}
                />
            ) : null}
            <div className={styles.body}>{children}</div>
        </div>
    );
};

export default InsightsModuleChrome;
